import { useState } from "react";
import { useCachedResource } from "../hooks/useCachedResource";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { PurchaseShell } from "../features/purchase/PurchaseShell";
import { Toolbar } from "../components/data/Toolbar";
import { DataTable } from "../components/data/DataTable";
import { RowActions } from "../components/data/RowActions";
import { NeuCard, NeuInput, NeuButton } from "../components/neu/Neu";
import { useBranchScope } from "../features/purchase/branchScope";
import { addBranchCenter, branchCenters, deleteBranchCenter } from "../api/gl";
import { localName } from "../lib/localName";

const EMPTY = { code: "", name_en: "", name_ar: "", type: "department" };

export default function PurchaseCostCentersPage() {
  const { t } = useTranslation();
  const { branchId } = useBranchScope();
  const [search, setSearch] = useState("");
  const [form, setForm] = useState({ ...EMPTY });
  const [open, setOpen] = useState(false);

  const { data, reload: load } = useCachedResource(
    `purch-centers-${branchId}`,
    () => (branchId ? branchCenters(branchId).then((d) => d.items || []).catch(() => []) : Promise.resolve([])),
    [branchId],
  );
  const rows = data || [];

  const save = async () => {
    try {
      await addBranchCenter(branchId, { ...form });
      toast.success(t("purchases.items.centerSaved"));
      setForm({ ...EMPTY }); setOpen(false); load();
    } catch (error) { toast.error(error?.response?.data?.detail?.code || t("errors.generic")); }
  };

  const columns = [
    { key: "code", label: t("purchases.items.centerCode"),
      render: (row) => <span className="num font-mono text-[11px] text-primary">{row.code}</span> },
    { key: "name", label: t("purchases.items.name"),
      render: (row) => <span className="text-xs font-bold">{localName(row)}</span> },
    { key: "state", label: t("purchases.cols.state"),
      render: (row) => (
        <span className={`text-[11px] font-bold ${row.is_system ? "text-primary" : "text-emerald-600"}`}>
          {row.is_system ? t("purchases.items.systemCenter") : t("purchases.supplierStates.active")}
        </span>) },
    { key: "actions", label: t("purchases.cols.actions"),
      render: (row) => (
        <RowActions testid={`center-actions-${row.id}`} actions={[{ key: "delete",
          title: t("app.delete"), disabled: row.is_system,
          onClick: async () => { await deleteBranchCenter(branchId, row.id); load(); } }]} />) },
  ];

  const filtered = rows.filter((row) =>
    !search || `${row.code} ${row.name_en} ${row.name_ar || ""}`.includes(search));

  return (
    <PurchaseShell titleKey="items">
      <Toolbar testid="center-toolbar" search={search} onSearch={setSearch}
               placeholder={t("purchases.items.searchCenter")}
               actionLabel={t("purchases.items.newCenter")}
               onAction={() => branchId && setOpen(!open)} />
      {open && branchId ? (
        <NeuCard className="grid gap-3 p-4 sm:grid-cols-4" data-testid="center-form">
          {["code", "name_en", "name_ar"].map((key) => (
            <NeuInput key={key} data-testid={`center-${key}`} value={form[key]}
                      placeholder={t(`purchases.items.${key === "code" ? "centerCode" : key === "name_en" ? "name" : "nameAr"}`)}
                      onChange={(e) => setForm({ ...form, [key]: e.target.value })} />
          ))}
          <NeuButton variant="primary" className="px-4 py-3 text-xs" data-testid="center-save"
                     disabled={!form.code || (form.name_en || "").trim().length < 2} onClick={save}>
            {t("app.save")}
          </NeuButton>
        </NeuCard>
      ) : null}
      <DataTable testid="centers-table" columns={columns} rows={filtered} rowKey={(row) => row.id}
                 empty={!branchId ? t("purchases.items.pickBranchFirst") : t("purchases.items.noCenters")} />
    </PurchaseShell>
  );
}
